import type { UpdateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import { zodResolver } from '@hookform/resolvers/zod';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import FormTextInput from 'components/forms/FormTextInput';
import type { VehicleSummary } from 'domain/vehicle';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import styles from './styles/VehicleDialog.module.scss';
import VehicleSubmitErrorAlert from './VehicleSubmitErrorAlert';
import { normalizeVehicleFields, requiredVehicleNameSchema, vehicleLicensePlateSchema } from './vehicleFormSchema';

const updateVehicleFormSchema = z.object({
    name: requiredVehicleNameSchema,
    licensePlate: vehicleLicensePlateSchema,
});

type UpdateVehicleFormData = z.infer<typeof updateVehicleFormSchema>;

interface UpdateVehicleFormProps {
    show: boolean;
    vehicle: VehicleSummary;
    isSubmitting: boolean;
    onUpdate: (vin: string, vehicle: UpdateVehicleTO) => void | Promise<void>;
    requestCancel: () => void;
    onDirtyChange: (isDirty: boolean) => void;
    renderDialog?: (form: ReactElement) => ReactNode;
}

const UpdateVehicleForm = ({
    show,
    vehicle,
    isSubmitting,
    onUpdate,
    requestCancel,
    onDirtyChange,
    renderDialog,
}: UpdateVehicleFormProps) => {
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<UpdateVehicleFormData>({
        resolver: zodResolver(updateVehicleFormSchema),
        mode: 'onChange',
        defaultValues: {
            name: vehicle.name,
            licensePlate: vehicle.licensePlate || '',
        },
    });

    const [submitFailed, setSubmitFailed] = useState(false);

    useEffect(() => {
        reset({
            name: vehicle.name,
            licensePlate: vehicle.licensePlate || '',
        });

        if (!show) {
            setSubmitFailed(false);
        }
    }, [show, reset, vehicle.name, vehicle.licensePlate]);

    useEffect(() => {
        onDirtyChange(isDirty);
    }, [isDirty, onDirtyChange]);

    const submitVehicle = async (formData: UpdateVehicleFormData) => {
        setSubmitFailed(false);

        try {
            await onUpdate(vehicle.vin, normalizeVehicleFields(formData));
        } catch {
            setSubmitFailed(true);
        }
    };

    const form = (
        <form className={`${styles.form} font-body`} onSubmit={handleSubmit(submitVehicle)}>
            <div className='display-flex flex-column gap-20 margin-bottom-20'>
                <div className='display-flex flex-column gap-15 margin-bottom-20'>
                    <div className='display-flex flex-column gap-15'>
                        <FormTextInput
                            id='vehicle-name'
                            label='Vehicle name'
                            placeholder='Start typing'
                            autoComplete='off'
                            mandatory
                            registration={register('name')}
                            error={errors.name?.message}
                            className='font-body'
                        />
                        <FormTextInput
                            id='vehicle-license-plate'
                            label='License plate'
                            placeholder='Start typing'
                            autoComplete='off'
                            registration={register('licensePlate')}
                            error={errors.licensePlate?.message}
                            className='font-body'
                        />
                    </div>
                </div>

                {submitFailed && (
                    <VehicleSubmitErrorAlert title='Failed to edit vehicle'>
                        Vehicle could not be edited due to a technical issue. Please try again or refresh the page. If
                        the error persists contact the control center support team.
                    </VehicleSubmitErrorAlert>
                )}
            </div>

            <FormDialogFooter
                onCancel={requestCancel}
                isValid={isValid && isDirty}
                isLoading={isSubmitting}
                onConfirmLabel='Save'
            />
        </form>
    );

    return renderDialog?.(form) ?? form;
};

export default UpdateVehicleForm;
